import {
  Button,
  Group,
  MultiSelect,
  MultiSelectProps,
  TextInput,
  Switch,
  Select,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { FormEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EventRecurrenceCreationForm } from 'src/Components/Forms/Event/EventForm/EventRecurrenceCreationForm/';
import { RoomSelector } from 'src/Components/Forms/Event/RoomSelector';
import AddressAutocomplete from 'src/Components/Shared/AddressAutocomplete/AddressAutocomplete.component';
import { MemberSelector } from 'src/Components/Shared/MemberSelector';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchHouseholds from 'src/Hooks/Queries/useFetchHouseholds';
import { EventDtoTypeEnum } from 'src/Services/API/generated';

import styles from './EventForm.module.scss';
import { Props } from './EventForm.types';

// t('createEventForm.eventTypes.OCCUPATION', "Occupation")
// t('createEventForm.eventTypes.ENTRETIEN', "Entretien")
// t('createEventForm.eventTypes.BILLING', "Facturation")
// t('createEventForm.eventTypes.LOCATION', "Location")
// t('createEventForm.eventTypes.MEETING', "Réunion")
// t('createEventForm.eventTypes.START_OF_CONTRACT', "Début de contrat")
// t('createEventForm.eventTypes.END_OF_CONTRACT', "Fin de contrat")
// t('createEventForm.eventTypes.WORK', "Travaux")
// t('createEventForm.eventTypes.ACTIVITIES', "Activités")
// t('createEventForm.eventTypes.OTHER', "Autre")
// t('createEventForm.eventTypes.PRACTICAL_INFORMATION', "Informations pratiques")
// t('createEventForm.eventTypes.PUBLIC_HOLIDAY', "Jours fériés")
// t('createEventForm.eventTypes.SCHOOL_HOLIDAY_ZONE_A', "Vacances Zone A")
// t('createEventForm.eventTypes.SCHOOL_HOLIDAY_ZONE_B', "Vacances Zone B")
// t('createEventForm.eventTypes.SCHOOL_HOLIDAY_ZONE_C', "Vacances Zone C")

// Keeps long option lists from pushing the sticky footer out of a mobile viewport.
const DROPDOWN_MAX_HEIGHT = 200;

const EXCLUDED_TYPES = [
  EventDtoTypeEnum.PublicHoliday,
  EventDtoTypeEnum.SchoolHolidayZoneA,
  EventDtoTypeEnum.SchoolHolidayZoneB,
  EventDtoTypeEnum.SchoolHolidayZoneC,
];

const EventForm = ({
  onCancel,
  form,
  onSubmit,
  submitButtonLabel,
  isReservation: _isReservation,
  initialAddressText,
  roomConflicts,
}: Props) => {
  const { t } = useTranslation();

  const reminderOptions = useMemo(() => {
    const reminderDurationsInSeconds = [
      0, // At time of event
      5 * 60, // 5 minutes
      10 * 60, // 10 minutes
      15 * 60, // 15 minutes
      30 * 60, // 30 minutes
      1 * 60 * 60, // 1 hour
      2 * 60 * 60, // 2 hours
      1 * 24 * 60 * 60, // 1 day
      2 * 24 * 60 * 60, // 2 days
    ];

    return reminderDurationsInSeconds.map(seconds => {
      if (seconds === 0) {
        return {
          label: t('createEventForm.reminder.atTimeOfEvent'),
          value: '0',
        };
      }
      return {
        label: `${dayjs.duration(seconds, 'seconds').humanize()} ${t('event.details.before')}`,
        value: seconds.toString(),
      };
    });
  }, [t]);

  const eventTypesOptions = Object.entries(EventDtoTypeEnum)
    .filter(([_, type]) => {
      // Exclude holiday types
      if ((EXCLUDED_TYPES as string[]).includes(type)) {
        return false;
      }
      return true;
    })
    .map(([key]) => {
      const value = EventDtoTypeEnum[key as keyof typeof EventDtoTypeEnum];
      return {
        value: value,
        label: t(`createEventForm.eventTypes.${value}`),
      };
    });

  const { data: asset } = useFetchAssetDetails();
  const { data: households } = useFetchHouseholds();

  const householdOptions = useMemo(
    () => households?.map(household => ({ value: household.id, label: household.name })) ?? [],
    [households]
  );

  const householdColorById = useMemo(() => {
    const map = new Map<string, string>();
    households?.forEach(household => map.set(household.id, household.color));
    return map;
  }, [households]);

  const renderHouseholdOption: MultiSelectProps['renderOption'] = ({ option }) => (
    <Group gap="sm">
      <span
        className={styles.householdColorDot}
        style={{ backgroundColor: householdColorById.get(option.value) }}
      />
      <span>{option.label}</span>
    </Group>
  );

  // Adding a household adds its registered members to the participants; removing one leaves
  // the participants untouched since they may have been picked by hand.
  const handleHouseholdsChange = useCallback(
    (householdIds: string[]) => {
      const addedIds = householdIds.filter(id => !form.values.householdIds.includes(id));
      const addedMemberIds =
        households
          ?.filter(household => addedIds.includes(household.id))
          .flatMap(household => household.members.map(member => member.userId))
          .filter((id): id is string => !!id) ?? [];

      form.setFieldValue('householdIds', householdIds);
      if (addedMemberIds.length > 0) {
        form.setFieldValue('participantIds', [
          ...new Set([...form.values.participantIds, ...addedMemberIds]),
        ]);
      }
    },
    [form, households]
  );

  const createEvent = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      if (!asset) {
        event.preventDefault();
        return;
      }

      // Never fail silently: on a phone the faulty field can be far off screen.
      const validation = form.validate();
      if (validation.hasErrors) {
        event.preventDefault();
        notifications.show({
          color: 'red',
          message: t(
            'createEventForm.errors.validationError',
            'Certains champs sont à corriger avant d’enregistrer.'
          ),
        });
        const [firstErrorPath] = Object.keys(validation.errors);
        form.getInputNode(firstErrorPath)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      return onSubmit(event);
    },
    [asset, form, onSubmit, t]
  );

  return (
    <>
      <form className={styles.fullFormContainer} onSubmit={createEvent}>
        <div className={styles.formContainer}>
          <TextInput
            placeholder={t('createEventForm.eventName', "Titre de l'événement")}
            {...form.getInputProps('name')}
          />
          <TextInput
            placeholder={t('createEventForm.description', "Motif de l'événement")}
            {...form.getInputProps('description')}
          />
          <AddressAutocomplete
            {...form.getInputProps('placeId')}
            placeholder={t('createEventForm.address', "Entrez l'adresse de l'événement")}
            description={t('createEventForm.addressDescription', 'Adresse (facultatif)')}
            initialAddressText={initialAddressText}
          />
          <TextInput
            placeholder={t(
              'createEventForm.videoConferenceUrlPlaceholder',
              'https://meet.google.com/...'
            )}
            description={t(
              'createEventForm.videoConferenceUrlDescription',
              'Lien de visio : collez ici un lien Zoom, Google Meet ou Teams, il s’affichera en bouton « Rejoindre la visio ».'
            )}
            {...form.getInputProps('videoConferenceUrl')}
          />
          <div className={styles.dateContainer}>
            <DateTimePicker
              style={{ width: '100%' }}
              label={t('createEventForm.startDateAndTime', 'Date et heure de début')}
              placeholder={t('createEventForm.startDateAndTimePlaceholder', 'Sélectionner')}
              {...form.getInputProps('startTime')}
              onChange={value => {
                // Mantine DateTimePicker may emit Date | string | null depending on version.
                const newStart = value ? dayjs(value as Date | string) : null;
                if (!newStart || !newStart.isValid()) {
                  return;
                }

                const oldStart = form.values.startTime ? dayjs(form.values.startTime) : null;
                const oldEnd = form.values.endTime ? dayjs(form.values.endTime) : null;
                const previousDurationMs =
                  oldStart && oldEnd && oldEnd.isAfter(oldStart) ? oldEnd.diff(oldStart) : null;
                const durationMs = previousDurationMs ?? 60 * 60 * 1000;

                form.setValues({
                  startTime: newStart.toDate(),
                  endTime: newStart.add(durationMs, 'millisecond').toDate(),
                });
              }}
            />
            <DateTimePicker
              style={{ width: '100%' }}
              label={t('createEventForm.endDateAndTime', 'Date et heure de fin')}
              placeholder={t('createEventForm.endDateAndTimePlaceholder', 'Sélectionner')}
              {...form.getInputProps('endTime')}
            />
          </div>
          <Select
            label={t('createEventForm.eventType', 'Type d’événement')}
            data={eventTypesOptions}
            {...form.getInputProps('type')}
            allowDeselect={false}
          />
          {householdOptions.length > 0 && (
            <MultiSelect
              label={t('createEventForm.households.label', 'Foyers concernés')}
              placeholder={t('createEventForm.households.placeholder', 'Sélectionner des foyers')}
              description={t(
                'createEventForm.households.description',
                'Les membres des foyers choisis sont ajoutés aux participants.'
              )}
              data={householdOptions}
              renderOption={renderHouseholdOption}
              value={form.values.householdIds}
              onChange={handleHouseholdsChange}
              maxDropdownHeight={DROPDOWN_MAX_HEIGHT}
              hidePickedOptions
              clearable
              searchable
            />
          )}
          {form.values.type === EventDtoTypeEnum.Occupation && (
            <RoomSelector
              label={t('createEventForm.rooms.label', 'Chambres occupées')}
              placeholder={t('createEventForm.rooms.placeholder', 'Sélectionner des chambres')}
              value={form.values.roomIds}
              onChange={roomIds => form.setFieldValue('roomIds', roomIds)}
              conflicts={roomConflicts}
            />
          )}
          <MemberSelector
            label={t('memberSelector.label', 'Participants')}
            placeholder={t('memberSelector.placeholder', 'Sélectionner des participants')}
            maxDropdownHeight={DROPDOWN_MAX_HEIGHT}
            {...form.getInputProps('participantIds')}
          />
          <Select
            label={t('createEventForm.eventReminder', 'Rappel')}
            data={reminderOptions}
            {...form.getInputProps('reminderDurationSeconds')}
            allowDeselect
            placeholder={t('createEventForm.noReminder')}
          />
          <div className={styles.tip}>
            {t(
              'createEventForm.reminderTooltip',
              'Seuls les participants à l’événement recevront un rappel.'
            )}
          </div>
          <Switch
            size="lg"
            label={t('createEventForm.exlusiveOccupation', 'Occupation exclusive du bien')}
            {...form.getInputProps('isExclusiveOccupation', { type: 'checkbox' })}
          />
        </div>
        <EventRecurrenceCreationForm
          form={form}
          paths={{
            frequency: 'recurrence.frequency',
            interval: 'recurrence.interval',
            customFrequency: 'recurrence.customFrequency',
            monthDayNumberOrAdvanced: 'recurrence.monthDayNumberOrAdvanced',
            weekDays: 'recurrence.byWeekDays',
            monthDayNumber: 'recurrence.monthDayNumber',
            month: 'recurrence.byMonth',
            advancedMonthDay: 'recurrence.byWeekDay',
            advancedMonthWeek: 'recurrence.byMonthDay',
            monthDaySelected: 'recurrence.monthDaySelected',
            until: 'recurrence.until',
          }}
        />
        <div className={styles.footer}>
          <Button variant="outline" onClick={onCancel}>
            {t('createEventForm.cancelEventCreation', 'Annuler')}
          </Button>
          <Button type="submit">{submitButtonLabel}</Button>
        </div>
      </form>
    </>
  );
};

export default EventForm;
